import React, { FormEvent, ReactNode, useEffect, useState } from 'react';
import { ApolloError } from '@apollo/client';
import {
  AuthenticationInput,
  InlineError,
  Typography,
} from 'glints-aries/es/@next';
import { Blue, Neutral } from 'glints-aries/es/@next/utilities/colors';
import { FormattedMessage, useIntl } from 'react-intl';

import { useVerifyWhatsappNumber } from '../../../../../common/hooks/requests/useVerifyWhatsappNumber';
import { getUser } from '../../../../../selectors/user';
import { useAppSelector } from '../../../../../store';
import { WhatsappVerificationStep } from '../../../../WhatsappVerification/types';
import { useWhatsappVerificationContext } from '../../../../WhatsappVerification/WhatsappVerificationContext/WhatsappVerificationContext';
import {
  FORM_ID,
  GraphQLErrorCodes,
  OTP_RESEND_WAIT_TIME_IN_SECONDS,
} from '../../consts';
import { errorMessages } from './constants';
import { StyledInputOTPStep } from './styles.sc';

export const InputOTPStep = () => {
  const { previousStep, setResendOTPCountdown, nextStep, setLoading } =
    useWhatsappVerificationContext();
  const user = useAppSelector(getUser);
  const { formatMessage } = useIntl();

  const [otpValue, setOtpValue] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [verifyWhatsappNumber] = useVerifyWhatsappNumber();

  useEffect(() => {
    setResendOTPCountdown(OTP_RESEND_WAIT_TIME_IN_SECONDS);
  }, [setResendOTPCountdown]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (otpValue?.length !== 6) {
      setError(formatMessage(errorMessages.required));
      return;
    }

    setLoading(true);
    await verifyWhatsappNumber({
      variables: {
        whatsappVerificationToken: otpValue || '',
      },
      onError: (e: ApolloError) => {
        const errorCode = e.graphQLErrors?.[0]?.extensions?.code;

        if (
          errorCode === GraphQLErrorCodes.INVALID_WHATSAPP_VERIFICATION_TOKEN
        ) {
          setError(formatMessage(errorMessages.wrongOTPEntered));
        } else {
          setError(formatMessage(errorMessages.somethingWentWrong));
        }
        setLoading(false);
      },
      onCompleted: () => {
        nextStep();
        setLoading(false);
      },
    });
  };

  return (
    <StyledInputOTPStep>
      <Typography variant="body1">
        <FormattedMessage
          id="text-enter-code-sent-whatsapp"
          defaultMessage="Please enter the 6-digit code has just been sent to the WhatsApp number:"
        />{' '}
        <Typography variant="body2">
          {user?.phone?.replace(/\s/g, '')}
        </Typography>
      </Typography>
      <Typography variant="body1" color={Neutral.B40}>
        <FormattedMessage
          id="text-is-number-correct"
          defaultMessage="Is the number incorrect? Update it <link>here</link>."
          values={{
            link: (text: ReactNode) => (
              <span onClick={previousStep}>
                <Typography variant="body1" color={Blue.S99} as="span">
                  {text}
                </Typography>
              </span>
            ),
          }}
        />
      </Typography>
      <form
        id={FORM_ID[WhatsappVerificationStep.InputOTP]}
        onSubmit={handleSubmit}
      >
        <Typography variant="subtitle2" as="label">
          <FormattedMessage id="text-enter-code" defaultMessage="Enter Code" />
        </Typography>
        <AuthenticationInput
          numberOfInputs={6}
          onChange={value => {
            setOtpValue(value);
            setError(null);
          }}
          error={Boolean(error)}
        />
      </form>
      <If condition={Boolean(error)}>
        <InlineError text={error || ''} data-cy={'error-message'} />
      </If>
    </StyledInputOTPStep>
  );
};
